// src/views/bots/flow/BotFlowBuilder.tsx
// Main Bot Flow Builder Component - Phase 5: Data Conversion

import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  addEdge,
  Connection,
  Node,
  Edge,
  ReactFlowInstance,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import styles from './BotFlowBuilder.module.css';
import logger from '../../../utils/logger';

// Custom node types
import StartNode from './nodes/StartNode';
import QuestionNode from './nodes/QuestionNode';

// Palette
import NodePalette from './palette/NodePalette';

// Converter
import { convertGroupsToFlow, convertFlowToGroups } from './utils/flowConverter';

// Types
import {
  BotFlowBuilderProps,
} from './types/flow.types';

// Header component
import BotFlowHeader from './components/BotFlowHeader';

// Confirmation Modal
import GenericConfirmationModal from '../../../components/GenericConfirmationModal';

// Preview
import BotFlowPreview from '../components/BotFlowPreview';

// Icons
import { Trash2, HelpCircle } from 'lucide-react';

// Toast
import toast from 'react-hot-toast';

// Tour & Hints
import BotFlowTour, { resetBotFlowTour } from './components/BotFlowTour';
import EmptyCanvasHint from './components/EmptyCanvasHint';
import Tooltip from '../../../components/Tooltip/Tooltip';

/**
 * Node type mapping for React Flow
 */
const nodeTypes: any = {
  startNode: StartNode,
  'interactive-button': QuestionNode,
  'interactive-list': QuestionNode,
  text: QuestionNode,
  email: QuestionNode,
  phone: QuestionNode,
  number: QuestionNode,
  location: QuestionNode,
  template: QuestionNode,
};

/**
 * Initial nodes for empty flow
 * Start node begins empty and selected so user can add their own keywords
 */
const initialNodes: Node[] = [
  {
    id: 'start',
    type: 'startNode',
    position: { x: 400, y: 50 },
    data: {
      keywords: [], // Empty - user must add their own keywords
    },
    selected: true, // Auto-select so user can immediately add keywords
  },
];

const initialEdges: Edge[] = [];

/**
 * Generate unique node ID
 * ✅ FIXED: Use consistent timestamp-based IDs everywhere
 */
const getNodeId = () => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substr(2, 9);
  return `node-${timestamp}-${random}`;
};

/**
 * 🤖 BOT FLOW BUILDER COMPONENT
 * Visual flow builder for creating WhatsApp bots
 */
const BotFlowBuilder: React.FC<BotFlowBuilderProps> = ({
  botId,
  initialGroups = [],
  onSave,
  onAutoSave, // Silent auto-save without navigation
  readOnly = false,
  initialBotData, // NEW: Bot name, description, active status
  onBack, // NEW: Back button handler
  onAnalytics, // NEW: Analytics button handler (edit mode only)
  isEdit = false, // NEW: Edit vs create mode
}) => {
  logger.log('🎨 BotFlowBuilder: Component rendered', {
    botId,
    hasInitialGroups: initialGroups.length > 0,
    readOnly,
  });

  // Refs
  const reactFlowWrapper = useRef<HTMLDivElement>(null);

  // Bot header state
  const [botName, setBotName] = useState(initialBotData?.name || '');
  const [botDescription, setBotDescription] = useState(initialBotData?.description || '');
  const [botIsActive, setBotIsActive] = useState(initialBotData?.isActive ?? true);
  const [botIsExistingLeadBot, setBotIsExistingLeadBot] = useState((initialBotData as any)?.isExistingLeadBot ?? false);
  const [isSaving, setIsSaving] = useState(false);

  // Auto-save state
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [lastAutoSaved, setLastAutoSaved] = useState<Date | null>(null);
  const [autoSaveError, setAutoSaveError] = useState<string | null>(null);
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const autoSaveRetryRef = useRef<NodeJS.Timeout | null>(null);
  const lastContentHashRef = useRef<string>('');
  const isUserEditingRef = useRef<boolean>(false);

  // Confirmation modal state
  const [showSaveModal, setShowSaveModal] = useState(false);

  // Preview state
  const [showPreview, setShowPreview] = useState(false);

  // Tour & Hints state
  // Only show canvas hint if tour is already completed — avoids overlapping the Start node on first visit
  const [showCanvasHint, setShowCanvasHint] = useState(() => {
    if (isEdit) return false;
    try {
      return localStorage.getItem('botFlowTourComplete') === 'true';
    } catch {
      return false;
    }
  });
  const [tourKey, setTourKey] = useState(0);

  // React Flow state management
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [reactFlowInstance, setReactFlowInstance] = useState<ReactFlowInstance | null>(null);

  // Edge selection feedback
  const [selectedEdgeCount, setSelectedEdgeCount] = useState(0);

  // Track connection drag source for optimistic auto-connect
  const connectingSourceRef = useRef<{ nodeId: string; handleId: string } | null>(null);

  /**
   * Generate a content hash from nodes/edges that ignores transient properties
   * (selected, position, dragging, etc.) - only tracks actual data changes
   */
  const getContentHash = useCallback(() => {
    // Extract only the data that matters for saving
    const nodeData = nodes.map(n => ({
      id: n.id,
      type: n.type,
      data: n.data, // The actual content
    }));

    const edgeData = edges.map(e => ({
      id: e.id,
      source: e.source,
      target: e.target,
      sourceHandle: e.sourceHandle,
      targetHandle: e.targetHandle,
      label: e.label,
    }));

    return JSON.stringify({
      botName,
      botDescription,
      botIsActive,
      botIsExistingLeadBot,
      nodes: nodeData,
      edges: edgeData,
    });
  }, [nodes, edges, botName, botDescription, botIsActive, botIsExistingLeadBot]);

  /**
   * Initialize content hash on mount (for edit mode auto-save comparison)
   */
  useEffect(() => {
    if (isEdit) {
      // Small delay to ensure nodes/edges are loaded
      setTimeout(() => {
        lastContentHashRef.current = getContentHash();
      }, 500);
    }
  }, [isEdit]); // Only run once on mount

  // Track if initial groups have been loaded (for auto-align trigger)
  const hasLoadedGroupsRef = useRef(false);
  const needsAutoAlignRef = useRef(false);

  /**
   * Load initial groups and convert to flow on mount
   * OR ensure start node is selected for new bots
   */
  useEffect(() => {
    if (initialGroups && initialGroups.length > 0) {
      logger.log('📥 BotFlowBuilder: Loading initial groups', {
        count: initialGroups.length,
      });

      // Get keywords from initialBotData if available
      const triggerKeywords = initialBotData?.triggerCondition?.keywords || [];

      const { nodes: convertedNodes, edges: convertedEdges } = convertGroupsToFlow(
        initialGroups,
        { keywords: triggerKeywords }
      );

      setNodes(convertedNodes);
      setEdges(convertedEdges);
      hasLoadedGroupsRef.current = true;
      needsAutoAlignRef.current = true; // Mark that we need auto-align
    } else {
      // New bot - make sure start node is selected and focused
      logger.log('🆕 BotFlowBuilder: New bot - selecting start node');
      setNodes((nds) =>
        nds.map((n) => ({
          ...n,
          selected: n.type === 'startNode',
        }))
      );
    }
  }, []); // Only run on mount

  /**
   * ✅ FIXED: Auto-align when reactFlowInstance becomes available after loading groups.
   * Previously used a 200ms setTimeout that fired before onInit set the instance.
   */
  useEffect(() => {
    if (reactFlowInstance && needsAutoAlignRef.current) {
      needsAutoAlignRef.current = false;
      // Small delay to let React Flow finish its internal layout
      setTimeout(() => {
        autoAlignNodes();
      }, 100);
    } else if (reactFlowInstance && !hasLoadedGroupsRef.current) {
      // New bot - fit view to show start node
      reactFlowInstance.fitView({
        padding: 0.15,
        maxZoom: 1.0,
        minZoom: 0.5,
      });
    }
  }, [reactFlowInstance]); // Triggers when React Flow initializes

  /**
   * Track edge selection for delete hint
   */
  useEffect(() => {
    const selectedEdges = edges.filter(edge => edge.selected);
    setSelectedEdgeCount(selectedEdges.length);
  }, [edges]);

  /**
   * Track whether user is actively editing (focused on input/textarea).
   * Auto-save is paused while editing to avoid saving mid-keystroke.
   * Uses document-level listener since focusin/focusout events bubble.
   */
  useEffect(() => {
    const handleFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) {
        isUserEditingRef.current = true;
      }
    };

    const handleFocusOut = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) {
        isUserEditingRef.current = false;
      }
    };

    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);

    return () => {
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
    };
  }, []);

  /**
   * Validate if connection is allowed
   */
  const isValidConnection = useCallback(
    (connection: Edge | Connection): boolean => {
      const { source, target } = connection;

      // Guard against null source/target
      if (!source || !target) return false;

      // Can't connect to self
      if (source === target) {
        logger.warn('🚫 BotFlowBuilder: Cannot connect node to itself');
        return false;
      }

      // Check for circular dependencies (simple check - would hit target, then source)
      const wouldCreateCycle = (start: string, end: string): boolean => {
        const visited = new Set<string>();
        const queue = [end];

        while (queue.length > 0) {
          const current = queue.shift()!;
          if (current === start) return true;
          if (visited.has(current)) continue;
          visited.add(current);

          // Find all edges from current node
          const outgoing = edges.filter((e) => e.source === current);
          queue.push(...outgoing.map((e) => e.target));
        }

        return false;
      };

      if (wouldCreateCycle(source, target)) {
        logger.warn('🚫 BotFlowBuilder: Would create circular dependency');
        return false;
      }

      return true;
    },
    [edges]
  );

  /**
   * Handle node connection with validation and labels
   */
  const onConnect = useCallback(
    (params: Connection) => {
      logger.log('🔗 BotFlowBuilder: Connecting nodes', params);

      // Validate connection
      if (!isValidConnection(params)) {
        return;
      }

      // Find source node to get label for branching connections
      const sourceNode = nodes.find((n) => n.id === params.source);
      let edgeLabel = '';
      let edgeColor = '#00a884'; // WhatsApp green

      if (sourceNode && params.sourceHandle?.startsWith('branch-')) {
        // This is a branching connection - find the option label and calculate color
        const optionId = params.sourceHandle.replace('branch-', '');

        // Extract options from correct structure
        let options: any[] = [];
        const interactive = sourceNode.data.interactive as any;
        if (sourceNode.data.questionType === 'interactive-button') {
          options = interactive?.action?.buttons?.map((btn: any) => ({
            id: btn.reply.id,
            title: btn.reply.title,
          })) || [];
        } else if (sourceNode.data.questionType === 'interactive-list') {
          interactive?.action?.sections?.forEach((section: any) => {
            section.rows?.forEach((row: any) => {
              options.push({
                id: row.id,
                title: row.title,
              });
            });
          });
        }

        const optionIndex = options.findIndex((opt: any) => opt.id === optionId);
        const option = options[optionIndex];

        if (option) {
          edgeLabel = option.title;
        }
      }

      // Create edge with label - thick + wide interaction zone
      const newEdge: Edge = {
        ...params,
        type: 'smoothstep',
        animated: true,
        label: edgeLabel || undefined,
        style: { stroke: edgeColor, strokeWidth: 3, cursor: 'pointer' },
        labelStyle: { fill: '#111b21', fontWeight: 600, fontSize: 14 },
        labelBgStyle: { fill: 'white', fillOpacity: 0.95, padding: 4 },
        interactionWidth: 20,
        focusable: true,
      } as Edge;

      setEdges((eds) => addEdge(newEdge, eds));
      logger.log('✅ BotFlowBuilder: Connection created', { label: edgeLabel });
    },
    [setEdges, isValidConnection, nodes]
  );

  /**
   * Track when user starts dragging a connection
   */
  const onConnectStart = useCallback(
    (_event: any, params: { nodeId: string | null; handleId: string | null }) => {
      if (params.nodeId && params.handleId) {
        connectingSourceRef.current = { nodeId: params.nodeId, handleId: params.handleId };
      }
    },
    []
  );

  /**
   * Optimistic auto-connect: when user releases a connection drag without hitting a target,
   * find the nearest valid unconnected node and auto-connect to it.
   */
  const onConnectEnd = useCallback(
    (event: any) => {
      const source = connectingSourceRef.current;
      if (!source || !reactFlowInstance || !reactFlowWrapper.current) {
        connectingSourceRef.current = null;
        return;
      }

      // Check if the connection was already made (React Flow handles this internally)
      // We only auto-connect if the drag ended on empty canvas
      const targetElement = (event as MouseEvent).target as HTMLElement;
      const isOnPane = targetElement?.classList?.contains('react-flow__pane');

      if (!isOnPane) {
        connectingSourceRef.current = null;
        return;
      }

      // Get mouse position in flow coordinates
      const bounds = reactFlowWrapper.current.getBoundingClientRect();
      const clientX = 'clientX' in event ? event.clientX : event.touches?.[0]?.clientX || 0;
      const clientY = 'clientY' in event ? event.clientY : event.touches?.[0]?.clientY || 0;
      const dropPosition = reactFlowInstance.screenToFlowPosition({
        x: clientX - bounds.left,
        y: clientY - bounds.top,
      });

      // Find the nearest node that:
      // 1. Is not the source node
      // 2. Has an input handle (question-input)
      // 3. Wouldn't create a cycle
      // 4. Isn't already connected from this specific source handle
      const sourceNodeId = source.nodeId;
      const sourceHandleId = source.handleId;

      // Get existing connections from this source handle
      const existingFromHandle = edges.filter(
        (e) => e.source === sourceNodeId && e.sourceHandle === sourceHandleId
      );

      // Find candidate nodes (anything with an input handle, i.e., not startNode)
      const candidateNodes = nodes.filter((n) => {
        if (n.id === sourceNodeId) return false;
        if (n.type === 'startNode') return false;

        // Check if already connected from this handle
        if (existingFromHandle.some((e) => e.target === n.id)) return false;

        return true;
      });

      if (candidateNodes.length === 0) {
        connectingSourceRef.current = null;
        return;
      }

      // Calculate distance from drop point to each candidate
      const withDistance = candidateNodes.map((n) => ({
        node: n,
        distance: Math.sqrt(
          Math.pow(n.position.x - dropPosition.x, 2) +
          Math.pow(n.position.y - dropPosition.y, 2)
        ),
      }));

      // Sort by distance and pick the closest within 400px
      withDistance.sort((a, b) => a.distance - b.distance);
      const nearest = withDistance[0];

      if (nearest && nearest.distance < 400) {
        // Validate: no cycles
        const testConnection = { source: sourceNodeId, target: nearest.node.id };
        if (isValidConnection(testConnection as Connection)) {
          // Auto-connect! Simulate onConnect
          const autoConnectParams: Connection = {
            source: sourceNodeId,
            target: nearest.node.id,
            sourceHandle: sourceHandleId,
            targetHandle: 'question-input',
          };

          logger.log('🎯 BotFlowBuilder: Optimistic auto-connect to nearest node', {
            target: nearest.node.id,
            distance: Math.round(nearest.distance),
          });

          onConnect(autoConnectParams);
        }
      }

      connectingSourceRef.current = null;
    },
    [reactFlowInstance, nodes, edges, isValidConnection, onConnect]
  );

  /**
   * Handle node click - SELECT the clicked node and ZOOM to it
   */
  const onNodeClick = useCallback((event: React.MouseEvent, node: Node) => {
    logger.log('👆 BotFlowBuilder: Node clicked', node.id);

    // SELECT clicked node, DESELECT all others
    setNodes((nds) =>
      nds.map((n) => ({
        ...n,
        selected: n.id === node.id,
      }))
    );

    // ZOOM to the clicked node for easier editing
    setTimeout(() => {
      if (reactFlowInstance) {
        reactFlowInstance.fitView({
          padding: 0.3,
          duration: 400,
          nodes: [node], // Focus on this specific node
          maxZoom: 1.2,   // Don't zoom in too much
          minZoom: 0.8,   // Don't zoom out too much
        });
      }
    }, 50);
  }, [setNodes, reactFlowInstance]);

  /**
   * Handle canvas click - deselect all nodes
   */
  const onPaneClick = useCallback(() => {
    logger.log('👆 BotFlowBuilder: Canvas clicked - deselecting all');

    // DESELECT all nodes when clicking canvas
    setNodes((nds) =>
      nds.map((n) => ({
        ...n,
        selected: false,
      }))
    );
  }, [setNodes]);

  /**
   * Handle drag over - required for drop to work
   */
  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  /**
   * Handle drop - create new node at drop position
   */
  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();

      const nodeType = event.dataTransfer.getData('application/reactflow');

      // Check if valid node type
      if (typeof nodeType === 'undefined' || !nodeType) {
        logger.warn('🚫 BotFlowBuilder: Invalid node type dropped');
        return;
      }

      // Get the drop position relative to the React Flow canvas
      const reactFlowBounds = reactFlowWrapper.current?.getBoundingClientRect();
      if (!reactFlowBounds || !reactFlowInstance) {
        logger.warn('🚫 BotFlowBuilder: Canvas not ready for drop');
        return;
      }

      const position = reactFlowInstance.screenToFlowPosition({
        x: event.clientX - reactFlowBounds.left,
        y: event.clientY - reactFlowBounds.top,
      });

      logger.log('➕ BotFlowBuilder: Creating node', { nodeType, position });

      // Create default data based on node type
      let nodeData: any = { questionType: nodeType };

      if (nodeType === 'startNode') {
        nodeData = { keywords: [] };
      } else if (nodeType === 'endNode') {
        nodeData = { message: '' };
      } else {
        // Question node
        nodeData = {
          questionType: nodeType,
          question: '',
          required: false,
        };

        // Add interactive property for button/list types with DEFAULT content
        if (nodeType === 'interactive-button') {
          nodeData.interactive = {
            type: 'button',
            action: {
              buttons: [
                {
                  type: 'reply',
                  reply: { id: `btn-${Date.now()}-1`, title: 'Option 1' },
                },
                {
                  type: 'reply',
                  reply: { id: `btn-${Date.now()}-2`, title: 'Option 2' },
                },
              ],
            },
          };
        } else if (nodeType === 'interactive-list') {
          nodeData.interactive = {
            type: 'list',
            header: { type: 'text', text: 'Choose an option' },
            body: { text: '' },
            action: {
              button: 'View Options',
              sections: [
                {
                  title: 'Section 1',
                  rows: [
                    { id: `row-${Date.now()}-1`, title: 'Option 1', description: '' },
                    { id: `row-${Date.now()}-2`, title: 'Option 2', description: '' },
                  ],
                },
              ],
            },
          };
        }
      }

      const newNode: Node = {
        id: getNodeId(),
        type: nodeType,
        position,
        data: nodeData,
      };

      // 🔥 UNSELECT all existing nodes, select new node
      setNodes((nds) => [
        ...nds.map(n => ({ ...n, selected: false })),
        { ...newNode, selected: true }
      ]);
    },
    [reactFlowInstance, setNodes]
  );

  // Node updates now handled inline within QuestionNode component using useReactFlow hook

  /**
   * Auto-align nodes using HORIZONTAL left-to-right layout with BRANCH handling (like n8n)
   */
  const autoAlignNodes = useCallback(() => {
    logger.log('🎯 BotFlowBuilder: Auto-aligning nodes (horizontal with branches)');

    // Configuration - HORIZONTAL LAYOUT
    const COLUMN_SPACING = 400; // Horizontal spacing between columns (left-to-right)
    const BRANCH_SPACING = 200; // Vertical spacing between branches
    const START_X = 100;
    const START_Y = 300;

    // Find start node
    const startNode = nodes.find((n) => n.type === 'startNode');
    if (!startNode) {
      logger.warn('🚫 BotFlowBuilder: No start node found');
      return;
    }

    // Track node positions with branch awareness
    const nodePositions = new Map<string, { x: number; y: number; depth: number; branchIndex: number }>();
    const visited = new Set<string>();

    // BFS with branch tracking
    const queue: Array<{ id: string; depth: number; branchIndex: number; parentBranchCount: number }> = [
      { id: startNode.id, depth: 0, branchIndex: 0, parentBranchCount: 1 },
    ];

    while (queue.length > 0) {
      const { id, depth, branchIndex, parentBranchCount } = queue.shift()!;

      if (visited.has(id)) continue;
      visited.add(id);

      // Store position info
      nodePositions.set(id, { x: 0, y: 0, depth, branchIndex }); // Will calculate x,y later

      // Find outgoing edges (branches from this node)
      const outgoingEdges = edges.filter((e) => e.source === id);
      const childCount = outgoingEdges.length;

      // Add children with branch awareness
      outgoingEdges.forEach((edge, index) => {
        if (!visited.has(edge.target)) {
          // Calculate branch index for this child
          // If this node has multiple children, spread them out
          const childBranchIndex = childCount > 1
            ? branchIndex + (index - Math.floor(childCount / 2))
            : branchIndex;

          queue.push({
            id: edge.target,
            depth: depth + 1,
            branchIndex: childBranchIndex,
            parentBranchCount: childCount
          });
        }
      });
    }

    // Calculate min/max branch indices to center the layout
    let minBranch = 0;
    let maxBranch = 0;
    nodePositions.forEach((pos) => {
      minBranch = Math.min(minBranch, pos.branchIndex);
      maxBranch = Math.max(maxBranch, pos.branchIndex);
    });

    // Calculate actual positions
    setNodes((nds) =>
      nds.map((node) => {
        const posInfo = nodePositions.get(node.id);
        if (!posInfo) return node; // Orphaned node - keep current position

        const x = START_X + posInfo.depth * COLUMN_SPACING; // LEFT → RIGHT based on depth
        const y = START_Y + (posInfo.branchIndex - minBranch) * BRANCH_SPACING; // Vertical based on branch

        return {
          ...node,
          position: { x, y },
        };
      })
    );

    // Fit view after layout with better padding to show more canvas
    setTimeout(() => {
      reactFlowInstance?.fitView({
        padding: 0.15,  // Less padding = more visible canvas
        duration: 800,
        maxZoom: 1.0,   // Don't zoom in too much
        minZoom: 0.5,   // Allow zooming out to see everything
      });
    }, 100);

    logger.log('✅ BotFlowBuilder: Horizontal auto-align with branches complete', {
      nodeCount: nodePositions.size,
      branchSpread: maxBranch - minBranch + 1,
    });
  }, [nodes, edges, setNodes, reactFlowInstance]);

  /**
   * Auto-save function - saves as draft without user confirmation
   */
  const performAutoSave = useCallback(async () => {
    // Don't auto-save if no name, already saving, or no auto-save handler
    if (!botName.trim() || isSaving || isAutoSaving || !onAutoSave) {
      return;
    }

    // Check if start node has keywords (required for valid bot)
    const startNode = nodes.find((n) => n.type === 'startNode');
    if (!startNode || !startNode.data.keywords || (startNode.data.keywords as string[]).length === 0) {
      return; // Don't auto-save without keywords
    }

    logger.log('💾 BotFlowBuilder: Auto-saving as draft...');
    setIsAutoSaving(true);
    setAutoSaveError(null);

    try {
      const { groups, triggerCondition } = convertFlowToGroups(nodes, edges);

      const botData = {
        name: botName.trim(),
        description: botDescription.trim(),
        isActive: botIsActive,
        isExistingLeadBot: botIsExistingLeadBot,
        // Preserve current status - don't downgrade active bots to draft
        triggerCondition,
        groups,
      };

      await onAutoSave(botData as any);
      setLastAutoSaved(new Date());
      logger.log('✅ BotFlowBuilder: Auto-save successful');
    } catch (error: any) {
      logger.error('❌ BotFlowBuilder: Auto-save failed', error);
      setAutoSaveError('Auto-save failed');
    } finally {
      setIsAutoSaving(false);
    }
  }, [botName, botDescription, botIsActive, botIsExistingLeadBot, nodes, edges, isSaving, isAutoSaving, onAutoSave]);

  /**
   * Debounced auto-save - triggers 15 seconds after ACTUAL content change
   * Ignores selection, position, hover changes
   * ✅ FIXED: Skips auto-save while user is actively editing (focused on input/textarea)
   */
  useEffect(() => {
    // Only auto-save in edit mode and if we have a valid bot name
    if (!isEdit || !botName.trim()) {
      return;
    }

    // Get current content hash
    const currentHash = getContentHash();

    // Skip if content hasn't actually changed
    if (currentHash === lastContentHashRef.current) {
      return;
    }

    // Clear existing timeout
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    // Schedule auto-save after 15 seconds of no changes (increased from 3s)
    autoSaveTimeoutRef.current = setTimeout(() => {
      // ✅ Skip if user is actively editing (typing in an input/textarea)
      if (isUserEditingRef.current) {
        logger.log('⏸️ BotFlowBuilder: Auto-save skipped - user is editing');
        // Retry in another 10 seconds — tracked separately so cleanup catches it
        autoSaveRetryRef.current = setTimeout(() => {
          if (!isUserEditingRef.current) {
            const finalHash = getContentHash();
            if (finalHash !== lastContentHashRef.current) {
              lastContentHashRef.current = finalHash;
              performAutoSave();
            }
          }
        }, 10000);
        return;
      }

      // Double-check content changed before saving
      const finalHash = getContentHash();
      if (finalHash !== lastContentHashRef.current) {
        lastContentHashRef.current = finalHash;
        performAutoSave();
      }
    }, 15000);

    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
      if (autoSaveRetryRef.current) {
        clearTimeout(autoSaveRetryRef.current);
      }
    };
  }, [isEdit, botName, getContentHash, performAutoSave]);

  /**
   * Handle save button click - Show confirmation modal
   */
  const handleSave = () => {
    logger.log('💾 BotFlowBuilder: Save clicked', {
      botName,
      nodesCount: nodes.length,
      edgesCount: edges.length,
    });

    // Validation
    if (!botName.trim()) {
      toast.error('Please enter a bot name');
      return;
    }

    // Validate start node has keywords
    const startNode = nodes.find((n) => n.type === 'startNode');
    if (!startNode || !startNode.data.keywords || (startNode.data.keywords as string[]).length === 0) {
      toast.error('Add at least one trigger keyword to the START node', { duration: 4000 });

      // Select the start node to help user
      setNodes((nds) =>
        nds.map((n) => ({
          ...n,
          selected: n.type === 'startNode',
        }))
      );
      return;
    }

    // Show confirmation modal
    setShowSaveModal(true);
  };

  /**
   * Execute actual save after confirmation
   */
  const executeSave = async () => {
    setIsSaving(true);

    try {
      // Convert flow to groups format
      const { groups, triggerCondition } = convertFlowToGroups(nodes, edges);

      logger.log('✅ BotFlowBuilder: Conversion complete', {
        groupCount: groups.length,
        keywords: triggerCondition.keywords.length,
      });

      // Prepare bot data
      const botData = {
        name: botName.trim(),
        description: botDescription.trim(),
        isActive: botIsActive,
        isExistingLeadBot: botIsExistingLeadBot,
        triggerCondition,
        groups,
      };

      // Call parent onSave callback
      if (onSave) {
        await onSave(botData as any);
        logger.log('✅ BotFlowBuilder: Save successful');
      } else {
        // No save handler - just show the data for now
        console.log('Bot data:', botData);
        alert(
          `Bot ready to save!\n\nName: ${botData.name}\nGroups: ${groups.length}\nKeywords: ${triggerCondition.keywords.length}\n\nCheck console for details.`
        );
      }
    } catch (error) {
      logger.error('❌ BotFlowBuilder: Save failed', error);
      toast.error('Failed to save bot. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Validation - bot name and start node must have keywords
  const startNodeHasKeywords: boolean = (() => {
    const startNode = nodes.find((n) => n.type === 'startNode');
    return !!(startNode && startNode.data.keywords && (startNode.data.keywords as string[]).length > 0);
  })();
  const isValid: boolean = botName.trim().length > 0 && nodes.length > 0 && startNodeHasKeywords;

  return (
    <div className={styles.flowBuilderContainer}>
      {/* Guided Tour */}
      <BotFlowTour key={tourKey} isEdit={isEdit} />

      {/* Bot Header - Name, Active, Description */}
      <BotFlowHeader
        name={botName}
        description={botDescription}
        isActive={botIsActive}
        isExistingLeadBot={botIsExistingLeadBot}
        onNameChange={setBotName}
        onDescriptionChange={setBotDescription}
        onActiveChange={setBotIsActive}
        onExistingLeadBotChange={setBotIsExistingLeadBot}
        onBack={onBack || (() => {})}
        onSave={handleSave}
        onAnalytics={onAnalytics}
        isSaving={isSaving}
        isValid={isValid}
        isEdit={isEdit}
        isAutoSaving={isAutoSaving}
        lastAutoSaved={lastAutoSaved}
        autoSaveError={autoSaveError}
      />

      {/* Toolbar */}
      <div className={styles.toolbar}>
        <div className={styles.toolbarLeft}>
          <h3 className={styles.toolbarTitle}>🎨 Visual Bot Flow</h3>
          <div className={styles.toolbarStats}>
            <span className={styles.stat}>
              <span className={styles.statIcon}>📦</span>
              {nodes.length} nodes
            </span>
            <span className={styles.stat}>
              <span className={styles.statIcon}>🔗</span>
              {edges.length} connections
            </span>
          </div>
        </div>

        <div className={styles.toolbarRight}>
          {!readOnly && (
            <>
              <Tooltip content="Automatically arrange all nodes in a clean left-to-right layout" position="bottom">
                <button
                  className={styles.btnSecondary}
                  onClick={autoAlignNodes}
                  data-tour="auto-align"
                >
                  ✨ Auto Align
                </button>
              </Tooltip>
              <Tooltip
                content={nodes.length <= 1 ? "Add at least one question node to preview your bot" : "Test your bot — see exactly how the WhatsApp conversation will look"}
                position="bottom"
              >
                <button
                  className={styles.btnPrimary}
                  onClick={() => setShowPreview(true)}
                  disabled={nodes.length <= 1}
                  data-tour="preview-btn"
                  style={{ background: 'linear-gradient(135deg, #075e54 0%, #128c7e 100%)', boxShadow: '0 4px 12px rgba(18, 140, 126, 0.3)' }}
                >
                  ▶ Preview Bot
                </button>
              </Tooltip>
              <Tooltip content="Replay the guided tour" position="bottom">
                <button
                  className={styles.helpButton}
                  onClick={() => {
                    resetBotFlowTour();
                    setTourKey(prev => prev + 1);
                  }}
                >
                  <HelpCircle size={18} />
                </button>
              </Tooltip>
            </>
          )}
        </div>
      </div>

      {/* Main Content: Palette + Canvas */}
      <div className={styles.flowContent}>
        {/* Node Palette - Left Sidebar */}
        {!readOnly && <NodePalette />}

        {/* React Flow Canvas - Drag & Drop Target */}
        <div
          className={styles.flowCanvas}
          ref={reactFlowWrapper}
          onDrop={(e) => { onDrop(e); setShowCanvasHint(false); }}
          onDragOver={onDragOver}
          data-tour="flow-canvas"
        >
          {/* Empty Canvas Getting-Started Hint */}
          {showCanvasHint && !isEdit && nodes.length <= 1 && !startNodeHasKeywords && (
            <EmptyCanvasHint onDismiss={() => setShowCanvasHint(false)} />
          )}
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onConnectStart={onConnectStart}
            onConnectEnd={onConnectEnd}
            onNodeClick={onNodeClick}
            onPaneClick={onPaneClick}
            onInit={setReactFlowInstance}
            nodeTypes={nodeTypes}
            isValidConnection={isValidConnection}
            connectionLineStyle={{
              stroke: '#00a884',
              strokeWidth: 3,
            }}
            connectionRadius={40}
            fitView
            proOptions={{ hideAttribution: true }}
            deleteKeyCode={['Backspace', 'Delete']}
            edgesReconnectable={true}
            edgesFocusable={true}
            panOnDrag={true} // Pan with any mouse button on empty canvas
            selectionOnDrag={false} // Disable box selection - click nodes to select
            selectionKeyCode="Shift" // Hold Shift to enable box selection
            multiSelectionKeyCode="Shift" // Hold Shift for multi-select
            defaultEdgeOptions={{
              style: {
                strokeWidth: 3,
                stroke: '#94a3b8',
                cursor: 'pointer',
              },
              interactionWidth: 20, // Invisible 20px hit zone prevents flicker
              focusable: true,
            }}
          >
            {/* Background pattern */}
            <Background color="#e5e7eb" gap={20} />

            {/* Controls (zoom, fit view, etc.) */}
            <Controls />

            {/* Minimap for navigation */}
            <MiniMap
              nodeColor={(node) => {
                switch (node.type) {
                  case 'startNode':
                    return '#10b981';
                  default:
                    return '#3b82f6';
                }
              }}
              maskColor="rgba(0, 0, 0, 0.1)"
            />
          </ReactFlow>
        </div>

        {/* Node Editor removed - now using inline editing on double-click */}
      </div>

      {/* Removed phase banner - production ready */}

      {/* Confirmation Modal */}
      <GenericConfirmationModal
        showModal={showSaveModal}
        toggleModal={() => setShowSaveModal(false)}
        onConfirmation={executeSave}
        title={isEdit ? '💾 Update Bot?' : '✨ Create Bot?'}
        message={
          isEdit
            ? `Are you sure you want to update "${botName}"?\n\nThis will replace the existing bot configuration.`
            : `Ready to create "${botName}"?\n\nKeywords: ${(nodes.find(n => n.type === 'startNode')?.data.keywords as string[] || []).length} trigger(s)\nFlow nodes: ${nodes.length - 1} step(s)`
        }
        confirmText={isEdit ? 'Update Bot' : 'Create Bot'}
        cancelText="Cancel"
      />

      {/* Delete Hint - Shows when connection is selected */}
      {selectedEdgeCount > 0 && (
        <div className={styles.deleteHint}>
          <Trash2 size={18} strokeWidth={2.5} />
          <span>Press <strong>Delete</strong> or <strong>Backspace</strong> to remove {selectedEdgeCount} connection{selectedEdgeCount > 1 ? 's' : ''}</span>
        </div>
      )}

      {/* Bot Flow Preview Modal */}
      {showPreview && (
        <BotFlowPreview
          botName={botName || 'Bot'}
          keywords={(nodes.find((n) => n.type === 'startNode')?.data.keywords as string[]) || []}
          nodes={nodes}
          edges={edges}
          onClose={() => setShowPreview(false)}
        />
      )}
    </div>
  );
};

export default BotFlowBuilder;
